import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import {
	RUNDOWN,
	type RundownResponseType,
	type RundownStateType,
	type RundownsResponseType,
} from "./types";

const rundownInitialState: RundownStateType = {
	calendar: {
		data: null,
		loading: true, // start with loading state to avoid flash
		error: null,
	},
	create: {
		data: null,
		submitting: false,
		success: null,
		error: null,
	},
	edit: {
		data: null,
		submitting: false,
		success: null,
		error: null,
	},
	detail: {
		data: null,
		deleting: false,
		loading: true,
		error: null,
		outputs: null,
	},
	live: {
		data: null,
		loading: true, // start with loading state to avoid flash
		error: null,
	},
	duplicate: {
		data: null,
		submitting: false,
		error: null,
	},
};

export const rundownSlice = createSlice({
	name: RUNDOWN,
	initialState: rundownInitialState,
	// Apply state changes
	reducers: {
		requestCreateRundown: (rundown) => {
			rundown.create.submitting = true;
			rundown.create.success = null;
			rundown.create.error = null;
		},
		receiveCreateRundown: (rundown, { payload }: PayloadAction<Rundown>) => {
			rundown.create.submitting = false;
			rundown.create.success = { completed: true, rundownId: payload.id };
			rundown.create.error = null;
			rundown.create.data = payload;
		},
		failedCreateRundown: (rundown, { payload: error }: PayloadAction<string>) => {
			rundown.create.submitting = false;
			rundown.create.success = null;
			rundown.create.error = error;
		},
		requestDeleteRundown: (rundown) => {
			rundown.detail.deleting = true;
			rundown.calendar.loading = true;
		},
		receiveDeleteRundown: (rundown, { payload: rundownId }: PayloadAction<string>) => {
			rundown.calendar.data =
				rundown.calendar.data?.filter((rundown) => rundown.id !== rundownId) || null;

			if (rundown.detail.data?.id === rundownId) {
				rundown.detail.data = null;
			}

			rundown.detail.deleting = false;
			rundown.detail.error = null;

			rundown.calendar.loading = false;
			rundown.calendar.error = null;
		},
		failedDeleteRundown: (rundown) => {
			rundown.detail.deleting = false;
			rundown.calendar.loading = false;
		},
		requestDuplicateRundown: (rundown) => {
			rundown.duplicate.submitting = true;
			rundown.duplicate.error = null;
		},
		receiveDuplicateRundown: (rundown, { payload }: PayloadAction<Rundown>) => {
			rundown.duplicate.submitting = false;
			rundown.duplicate.error = null;

			// Both the calendar and the persistent overview render from calendar.data.
			if (!rundown.calendar.data) return;
			rundown.calendar.data.push(payload);
		},
		failedDuplicateRundown: (rundown, { payload: error }: PayloadAction<string>) => {
			rundown.duplicate.submitting = false;
			rundown.duplicate.error = error;
		},
		requestUpdateRundown: (rundown) => {
			rundown.edit.submitting = true;
			rundown.edit.success = null;
			rundown.edit.error = null;
			rundown.detail.loading = true;
		},
		receiveUpdateRundown: (rundown, { payload: updatedRundown }: PayloadAction<Rundown>) => {
			rundown.edit.submitting = false;
			rundown.edit.success = { completed: true, rundownId: updatedRundown.id };
			rundown.edit.error = null;

			rundown.detail.loading = false;
			rundown.detail.error = null;

			if (!updatedRundown || !rundown.detail.data) return;
			rundown.detail.data =
				rundown.detail.data.id === updatedRundown.id ? updatedRundown : rundown.detail.data;
		},
		failedUpdateRundown: (rundown) => {
			rundown.edit.submitting = false;
			rundown.edit.success = null;
			rundown.detail.loading = false;
		},
		requestGetLiveRundowns: (rundown) => {
			rundown.live.loading = true;
		},
		receiveGetLiveRundowns: (rundown, { payload }: PayloadAction<RundownsResponseType>) => {
			rundown.live.loading = false;
			rundown.live.data = payload;
			rundown.live.error = null;
		},
		failedGetLiveRundowns: (rundown, { payload: error }: PayloadAction<string>) => {
			rundown.live.loading = false;
			rundown.live.error = error;
		},
		requestGetRundowns: (rundown) => {
			rundown.calendar.loading = true;
		},
		receiveGetRundowns: (rundown, { payload }: PayloadAction<RundownsResponseType>) => {
			rundown.calendar.loading = false;
			rundown.calendar.data = payload;
			rundown.calendar.error = null;
		},
		failedGetRundowns: (rundown, { payload: error }: PayloadAction<string>) => {
			rundown.calendar.loading = false;
			rundown.calendar.error = error;
		},
		requestGetRundown: (rundown) => {
			rundown.detail.loading = true;
		},
		receiveGetRundown: (rundown, { payload }: PayloadAction<RundownResponseType | null>) => {
			rundown.detail.loading = false;
			rundown.detail.data = payload;
			rundown.detail.error = null;

			if (!payload || !rundown.detail.data) return;

			rundown.detail.data.graphicPackageId = payload.graphicPackage?.id as string;
			rundown.detail.data.rundownTemplateId = payload.rundownTemplate?.id || "blank";
		},
		failedGetRundown: (rundown, { payload: error }: PayloadAction<string>) => {
			rundown.detail.loading = false;
			rundown.detail.error = error;
		},
		updateCurrentRundown: (rundown, { payload }: PayloadAction<RundownResponseType>) => {
			if (!rundown.detail.data) return;
			Object.assign(rundown.detail.data, payload);
		},
		failedGetActiveGraphicsRundown: () => {},
		requestGoLiveRundown: (rundown) => {
			if (!rundown.detail.data) return;
			rundown.detail.data.isLive = true;
		},
		receiveGoLiveRundown: (rundown) => {
			if (!rundown.detail.data) return;
			rundown.detail.data.isLive = true;
		},
		failedGoLiveRundown: (rundown) => {
			if (!rundown.detail.data) return;
			rundown.detail.data.isLive = false;
		},
		requestGoOfflineRundown: (rundown) => {
			if (!rundown.detail.data) return;
			rundown.detail.data.isLive = true;
		},
		receiveGoOfflineRundown: (rundown) => {
			if (!rundown.detail.data) return;
			rundown.detail.data.isLive = true;
		},
		failedGoOfflineRundown: (rundown) => {
			if (!rundown.detail.data) return;
			rundown.detail.data.isLive = false;
		},
		requestUpdateRundownOperator: () => {},
		receiveUpdateRundownOperator: () => {},
		failedUpdateRundownOperator: () => {},
		requestGetOutputsRundown: () => {},
		receiveGetOutputsRundown: (rundown, { payload }: PayloadAction<RundownOutput[]>) => {
			rundown.detail.outputs = payload;
			rundown.detail.error = null;
		},
		failedGetOutputsRundown: () => {},
		clearRundownSuccess: (rundown) => {
			rundown.create.success = null;
			rundown.edit.success = null;
		},
	},
});

const actions = rundownSlice.actions;
const rundownReducer = rundownSlice.reducer;

export { actions, rundownReducer };
